import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Smartphone, Copy, QrCode, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useTwoFactor } from '@/hooks/useTwoFactor';
import type { TwoFactorSetupResponse } from '@/types/twoFactor';

interface TwoFactorSetupProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  setupData: TwoFactorSetupResponse | null;
}

export const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({
  isOpen,
  onClose,
  onComplete,
  setupData,
}) => {
  const { completeSetup, isCompleting, error: setupError, clearError } = useTwoFactor();
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState<'scan' | 'verify'>('scan');
  const [copied, setCopied] = useState(false);

  const handleCopySecret = async () => {
    if (setupData?.manual_entry_key) {
      try {
        await navigator.clipboard.writeText(setupData.manual_entry_key);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
      }
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationCode.trim()) return;

    try {
      const success = await completeSetup(verificationCode.trim());
      if (success) {
        onComplete();
        setStep('scan');
        setVerificationCode('');
      }
    } catch (err) {
      console.error('Failed to complete 2FA setup:', err);
    }
  };

  const handleClose = () => {
    setStep('scan');
    setVerificationCode('');
    clearError();
    onClose();
  };

  const handleProceedToVerify = () => {
    setStep('verify');
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Enable Two-Factor Authentication
          </DialogTitle>
          <DialogDescription>
            {step === 'scan' 
              ? 'Scan the QR code with your authenticator app or enter the code manually.'
              : 'Enter the 6-digit code from your authenticator app to complete setup.'
            }
          </DialogDescription>
        </DialogHeader>

        {setupError && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{setupError}</AlertDescription>
          </Alert>
        )}

        {step === 'scan' && setupData && (
          <div className="space-y-6">
            <div className="text-sm text-muted-foreground">
              <p className="mb-2">Step 1: Install an authenticator app if you haven't already:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Google Authenticator</li>
                <li>Microsoft Authenticator</li>
                <li>Authy</li>
                <li>1Password</li>
              </ul>
            </div>

            <div className="text-sm text-muted-foreground">
              <p>Step 2: Set up your account in the authenticator app:</p>
            </div>

            <Tabs defaultValue="qr" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="qr" className="flex items-center gap-1">
                  <QrCode className="h-4 w-4" />
                  QR Code
                </TabsTrigger>
                <TabsTrigger value="manual">Manual Entry</TabsTrigger>
              </TabsList>

              <TabsContent value="qr" className="space-y-4">
                <Card>
                  <CardContent className="flex flex-col items-center p-6">
                    <div className="bg-white p-4 rounded-lg border">
                      <img
                        src={setupData.qr_code_url}
                        alt="2FA QR Code"
                        className="w-48 h-48"
                        data-testid="qr-code-image"
                      />
                    </div>
                    <p className="text-sm text-muted-foreground mt-2 text-center">
                      Scan this QR code with your authenticator app
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="manual" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Manual Setup</CardTitle>
                    <CardDescription>
                      Enter this code in your authenticator app manually
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Account Name</Label>
                      <div className="font-mono text-sm p-2 bg-muted rounded border">
                        Hirelo
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Secret Key</Label>
                      <div className="flex gap-2">
                        <div className="font-mono text-sm p-2 bg-muted rounded border flex-1 break-all">
                          {setupData.manual_entry_key}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCopySecret}
                          className="flex items-center gap-1"
                          data-testid="copy-secret-button"
                        >
                          {copied ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                          {copied ? 'Copied' : 'Copy'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            <div className="flex justify-end">
              <Button 
                onClick={handleProceedToVerify}
                data-testid="proceed-to-verify-button"
              >
                I've Added the Account
              </Button>
            </div>
          </div>
        )}

        {step === 'verify' && (
          <form onSubmit={handleVerifyCode} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="verification-code">Verification Code</Label>
              <Input
                id="verification-code"
                type="text"
                placeholder="Enter 6-digit code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="text-center text-lg font-mono"
                autoComplete="off"
                data-testid="verification-code-input"
              />
              <p className="text-sm text-muted-foreground">
                Enter the 6-digit code from your authenticator app
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep('scan')}
                data-testid="back-to-setup-button"
              >
                Back
              </Button>
              <Button
                type="submit"
                disabled={verificationCode.length !== 6 || isCompleting}
                data-testid="verify-and-enable-button"
              >
                {isCompleting ? 'Verifying...' : 'Verify & Enable'}
              </Button>
            </div>
          </form>
        )}

        <div className="text-xs text-muted-foreground">
          <p>
            Make sure your device's time is accurate for the codes to work properly.
            Time-based codes refresh every 30 seconds.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};